import type { LookupUser, LookupUsers } from '@atlassian/ui-modifications-core/types';
import { FieldValueLookupError, isNotAutomaticId } from '@atlassian/ui-modifications-core/utils';
import { isNotEmptyString } from '@atlassian/ui-modifications-public-api-utils/is-not-empty-string';
import FeatureGates from '@atlaskit/feature-gate-js-client';

export type UserPickerPublicSetValue = null | string;

export type UserPickerPublicGetValue = null | {
    accountId: string;
};

export interface UserPickerInternalValue {
    label: string;
    email: string;
    iconUrl?: string;
    accountId: string;
    value: string;
}

export type UserPickerTransformedValue = UserPickerInternalValue | null;

/**
 * Finds a user in the lookup data by their account ID.
 *
 * @param lookupData - Array of users to search through
 * @param accountId - The account ID to search for
 * @returns The matching user object or null if not found
 */
const findUserLookupValue = (lookupData: LookupUsers, accountId: string): LookupUser | null => {
    if (!lookupData || !Array.isArray(lookupData)) {
        return null;
    }

    return lookupData.find((user) => user.accountId === accountId) || null;
};

/**
 * Validates that a value is a valid user picker set value.
 * A valid value is either null or a non-empty string (account ID) that is not an automatic ID.
 *
 * @param value - The value to validate
 * @returns True if the value is valid, false otherwise
 */
export const validateUserPickerSetValue = (value: unknown): value is UserPickerPublicSetValue =>
    value === null || (isNotEmptyString(value) && isNotAutomaticId(value));

/**
 * Transforms a public user picker value (account ID) into an internal user object.
 * Looks up the user details from the provided lookup data and creates a complete user object.
 *
 * @param value - The public value to transform (account ID or null)
 * @param lookupValues - Array of user lookup data to search through
 * @returns Transformed user object with all user details, or null if value is null
 * @throws {FieldValueLookupError} When the user cannot be found in the lookup data
 */
export const userPickerSetValueTransformer = (
    value: UserPickerPublicSetValue,
    lookupValues?: LookupUsers
): UserPickerTransformedValue => {
    if (value === null) {
        return null;
    }

    const foundUser = findUserLookupValue(lookupValues || [], value);

    if (!foundUser) {
        throw new FieldValueLookupError(`Could not find user with accountId: ${value}`);
    }

    return {
        label: foundUser.name,
        email: '', // Email not available in LookupUser, update this once we have a way to get the email
        iconUrl: foundUser.picture,
        accountId: foundUser.accountId,
        value: foundUser.accountId,
    };
};

/**
 * Transforms an internal user object back to a public get value.
 * Extracts only the account ID from the complete user object for public API consumption.
 *
 * @param value - The internal user object to transform
 * @returns Public get value containing only the account ID, or null if value is falsy
 */
export const userPickerGetValueTransformer = (value: UserPickerTransformedValue): UserPickerPublicGetValue => {
    if (!value || (FeatureGates.checkGate('ditto_fix_getvalue_empty_state') && !value.accountId)) {
        return null;
    }

    return { accountId: value.accountId };
};

/**
 * Converts a transformed user object back to its basic form (account ID).
 * This is used to simplify the user object back to just the essential identifier.
 *
 * @param value - The transformed user object to convert, or null
 * @returns The account ID as a string, or null if the input value is falsy
 */
export const modifyUserPickerValueToBasic = (value: UserPickerTransformedValue | null): UserPickerPublicSetValue => {
    if (!value) {
        return null;
    }
    return value.accountId;
};
